import axios from 'axios';
import {BehaviorSubject} from 'rxjs';
import {NotificationManager} from 'react-notifications';
import {applyPlayersMessage} from './playerUpdates';

const FileDownload = require('js-file-download');

const ModalType = Object.freeze({
    PRECALL: 0,
    REPORT: 1
});

export const UserScope = Object.freeze({
  CLIENT_VIEWER: 0,
  CLIENT_ADMIN: 1,
  MIAI_VIEWER: 2,
  MIAI_ADMIN: 3,
  NON_EGM_PLAYER: 4
});

const AUTH_STORAGE_KEY = 'gbb_admin_auth';
const WS_RECONNECT_BASE_DELAY_MS = 1000;
const WS_RECONNECT_MAX_DELAY_MS = 30000;
const WS_STALE_AFTER_MS = 45000;
const WS_STALE_CHECK_INTERVAL_MS = 10000;

const isAdminScope = (scope) => scope === UserScope.CLIENT_ADMIN || scope === UserScope.MIAI_ADMIN;

const serverIntf = (function () {
    let once = false;
    let isWaiting = false;
    let api;
    let retStruct;

    // enrollments functions 
    let enroll;
    let enable;
    let unenroll;
    let ignoreEss;
    let navRules;

    // Context handling
    let context;
    let contextSubject;
    let contextSubjectSubscribe;

    // Pinwheel handling
    let waitingSubject;
    let waitingSubjectSubscribe;

    // Modal handling
    let modalSubj;
    let modalSubjSubscrbr;
    let setModalState;

    // Precall handling
    let precallSubj;
    let precallSubjSubscrbr;

    // ESS handling
    let ess;
    let essSubject;
    let essSubjectSubscribe;
    let setESS;

    // Websocket
    let ws;
    let wsReconnectTimer;
    let wsReconnectAttempt = 0;
    let wsLastMessageAt = 0;
    let wsHealthTimer;
    let wsShouldReconnect = false;
    let wsReconnectNoticeShown = false;
    let authExpiryTimer;

    // Egm handling
    let egmSubject;
    let egmSubjectSubscribe;

    // Recall handling
    let recallSubject;
    let recallSubjectSubscribe;
    let recall;
    let setRecall;
    let publishRecallForESS;
    let publishPlayersMessage;

    // Login handling
    let usr;
    let login;
    let usermod;
    let loginSubject;
    let loginSubjectSubscribe;
    let anonymousLoginState;

    // Wager handling
    let getWagers;
    let wagerSubject;
    let wagerSubjectSubscribe;

    // User handling
    let users = [];
    let getUsers;
    let modifyUser;
    let deleteUser;
    let userSubject;
    let userSubjectSubscribe;
    let getConfig;
    let updateConfig;
    let getStagedConfig;
    let upsertStagedConfig;
    let deleteStagedConfig;

    // Generic method
    let get;
    let post;

    // report handling
    let sessionReport;
    let enrollmentReport;
    let deckReport;
    let auditReport;

    const persistAuth = (nextUsr) => {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return;
            if (!nextUsr || !nextUsr.Token) {
                window.localStorage.removeItem(AUTH_STORAGE_KEY);
                return;
            }
            const payload = {
                Token: nextUsr.Token,
                Admin: !!nextUsr.Admin,
                Scope: Number.isInteger(nextUsr.Scope) ? nextUsr.Scope : null,
                EmployeeID: Number.isInteger(nextUsr.EmployeeID) ? nextUsr.EmployeeID : 0,
                WagerID: Number.isInteger(nextUsr.WagerID) ? nextUsr.WagerID : null,
                PlayerESS: typeof nextUsr.PlayerESS === 'string' ? nextUsr.PlayerESS : '',
                Expires: Number.isInteger(nextUsr.Expires) ? nextUsr.Expires : 0
            };
            window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
        } catch {
            // ignore storage access issues
        }
    };

    const restoreAuth = () => {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return null;
            const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed.Token !== 'string' || parsed.Token.trim() === '') {
                window.localStorage.removeItem(AUTH_STORAGE_KEY);
                return null;
            }
            if (Number.isInteger(parsed.Expires) && parsed.Expires > 0 && Date.now() > parsed.Expires) {
                window.localStorage.removeItem(AUTH_STORAGE_KEY);
                return null;
            }
            return parsed;
        } catch {
            return null;
        }
    };

    const clearWsReconnectTimer = () => {
        if (!wsReconnectTimer) {
            return;
        }
        clearTimeout(wsReconnectTimer);
        wsReconnectTimer = null;
    };

    const clearWsHealthTimer = () => {
        if (!wsHealthTimer) {
            return;
        }
        clearInterval(wsHealthTimer);
        wsHealthTimer = null;
    };

    const clearAuthExpiryTimer = () => {
        if (!authExpiryTimer) {
            return;
        }
        clearTimeout(authExpiryTimer);
        authExpiryTimer = null;
    };

    const stopWs = () => {
        const socket = ws;
        ws = null;
        wsLastMessageAt = 0;
        if (!socket) {
            return;
        }
        try {
            socket.close();
        } catch (_) {
            // ignore socket close failures
        }
    };

    const signoff = (opts = {}) => {
        const localOnly = !!opts.localOnly;
        const tok = usr && usr.Token ? usr.Token : null;
        wsShouldReconnect = false;
        clearWsReconnectTimer();
        clearWsHealthTimer();
        clearAuthExpiryTimer();
        if (tok && !localOnly) {
            api.post('/logout', {}, { headers: { Authorization: tok } }).catch(() => {});
        }
        stopWs();
        usr = null;
        persistAuth(null);
        essSubject.next('');
        publishPlayersMessage({type: 'snapshot', egms: []});
        recallSubject.next({});
        loginSubject.next(anonymousLoginState || {success: false, admin: false, scope: null, playerESS: '', wagerID: null});
        waitingSubject.next(false);
    };

    const ensureAuthIsCurrent = () => {
        if (!usr || !usr.Token) {
            return false;
        }
        if (Number.isInteger(usr.Expires) && usr.Expires > 0 && Date.now() >= usr.Expires) {
            signoff({ localOnly: true });
            return false;
        }
        return true;
    };

    const scheduleAuthExpiry = () => {
        clearAuthExpiryTimer();
        if (!ensureAuthIsCurrent() || !Number.isInteger(usr.Expires) || usr.Expires <= 0) {
            return;
        }
        authExpiryTimer = setTimeout(() => {
            signoff({ localOnly: true });
        }, Math.max(usr.Expires - Date.now(), 0));
    };

    const scheduleWsReconnect = (reason) => {
        if (!wsShouldReconnect || wsReconnectTimer || !ensureAuthIsCurrent()) {
            return;
        }
        const delay = Math.min(WS_RECONNECT_BASE_DELAY_MS * (2 ** wsReconnectAttempt), WS_RECONNECT_MAX_DELAY_MS);
        wsReconnectAttempt = Math.min(wsReconnectAttempt + 1, 5);
        if (!wsReconnectNoticeShown) {
            warning('Live Updates Interrupted', 'Trying to reconnect to the websocket.', 1500);
            wsReconnectNoticeShown = true;
        }
        console.log(`Websocket reconnect scheduled in ${delay}ms (${reason})`);
        wsReconnectTimer = setTimeout(() => {
            wsReconnectTimer = null;
            activateWs();
        }, delay);
    };

    const startWsHealthMonitor = () => {
        if (wsHealthTimer) {
            return;
        }
        wsHealthTimer = setInterval(() => {
            if (!wsShouldReconnect || !ws || ws.readyState !== WebSocket.OPEN || wsLastMessageAt <= 0) {
                return;
            }
            if (Date.now() - wsLastMessageAt < WS_STALE_AFTER_MS) {
                return;
            }
            console.warn('Websocket stale; forcing reconnect');
            stopWs();
            scheduleWsReconnect('stale');
        }, WS_STALE_CHECK_INTERVAL_MS);
    };

    // Initialization login to publish the login state from server
    const initlogin = () => {
        anonymousLoginState = {success: false, admin: false, scope: null, playerESS: '', wagerID: null};
        loginSubject = new BehaviorSubject(anonymousLoginState);
        loginSubjectSubscribe = (subscriber) => {
            return loginSubject.subscribe(subscriber);
        };

        waitingSubject = new BehaviorSubject(false);
        waitingSubjectSubscribe = (subscriber) => {
            return waitingSubject.subscribe(subscriber);
        };

        login = (user, pass) => {
            waitingSubject.next(true);
            return api.post('/login', {}, {auth: {username: user, password: pass}})
                .then((response) => {
                    usr = response.data;
                    persistAuth(usr);
                    wsShouldReconnect = true;
                    scheduleAuthExpiry();
                    loginSubject.next({
                        success: true,
                        admin: response.data.Admin,
                        scope: Number.isInteger(response.data.Scope) ? response.data.Scope : null,
                        playerESS: typeof response.data.PlayerESS === 'string' ? response.data.PlayerESS : '',
                        wagerID: Number.isInteger(response.data.WagerID) ? response.data.WagerID : null
                    });
                    waitingSubject.next(false);
                    activateWs();
                }).catch(err => {
                    const status = err && err.response ? err.response.status : 0;
                    const body = err && err.response ? err.response.data : '';
                    const isPasswordExpired = status === 403 && typeof body === 'string' && body.toLowerCase().includes('password expired');
                    const isClientLicenseRequired = status === 403 && typeof body === 'string' && body.toLowerCase().includes('client license required');
                    if (isPasswordExpired) {
                        warning('Password Expired', 'Your password has expired. Please reset your password to continue.', 2500);
                        window.history.replaceState({}, '', '/PasswordReset');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                    } else if (isClientLicenseRequired) {
                        waitingSubject.next(false);
                        const license = window.prompt('Client license required. Enter your client license to continue.');
                        if (!license || license.trim() === '') {
                            parseError(err, 1500);
                            usr = null;
                            persistAuth(null);
                            clearAuthExpiryTimer();
                            loginSubject.next(anonymousLoginState);
                            throw (err);
                        }
                        waitingSubject.next(true);
                        return api.post('/complete-license', {license: license.trim()}, {auth: {username: user, password: pass}})
                            .then(() => {
                                waitingSubject.next(false);
                                return login(user, pass);
                            })
                            .catch(saveErr => {
                                parseError(saveErr, 1500);
                                usr = null;
                                persistAuth(null);
                                clearAuthExpiryTimer();
                                loginSubject.next(anonymousLoginState);
                                waitingSubject.next(false);
                                throw (saveErr);
                            });
                    } else {
                        parseError(err, 1500);
                    }
                    usr = null;
                    persistAuth(null);
                    clearAuthExpiryTimer();
                    loginSubject.next(anonymousLoginState);
                    waitingSubject.next(false);
                    throw (err);
                });
        };

        const restoredUsr = restoreAuth();
        if (restoredUsr && restoredUsr.Token) {
            usr = restoredUsr;
            wsShouldReconnect = true;
            scheduleAuthExpiry();
            loginSubject.next({
                success: true,
                admin: !!restoredUsr.Admin,
                scope: Number.isInteger(restoredUsr.Scope) ? restoredUsr.Scope : null,
                playerESS: typeof restoredUsr.PlayerESS === 'string' ? restoredUsr.PlayerESS : '',
                wagerID: Number.isInteger(restoredUsr.WagerID) ? restoredUsr.WagerID : null
            });
            activateWs();
        }

        usermod = (user, email, phone, license, pass) => {
            if (phone !== '') {
                let usr = {
                    username: user,
                    email: email,
                    phone: phone,
                    license: license,
                    password: pass
                };
                waitingSubject.next(true);
                return api.post('/register', usr)
                    .then(() => {
                        waitingSubject.next(false);
                        window.location.pathname = '/';
                    }).catch(err => {
                        parseError(err, 1500);
                        waitingSubject.next(false);
                        throw (err);
                    });
            } else {
                waitingSubject.next(true);
                return api.post('/password-reset', {email: email}, {auth: {username: user, password: pass}})
                    .then(() => {
                        waitingSubject.next(false);
                        window.location.pathname = '/';
                    }).catch(err => {
                        parseError(err, 1500);
                        waitingSubject.next(false);
                        throw (err);
                    });

            }
        };

        const request = (url, data, method, isBlob, background) => {
            waitingSubject.next(!background);
            let config = {
                url: url,
                data: data,
                method: method,
            };

            if (isBlob)
                config.responseType = 'blob';

            if (usr) {
                if (usr.Token)
                    config.headers = {'Authorization': usr.Token};
            }

            return api.request(config).catch((error) => {
                if (error && error.response) {
                    if (error.response.status === 401 || error.response.status === 403) {
                        if (usr.Token)
                            signoff();
                    }
                }
                throw (error);
            });
        };

        get = (url, isBlob, background) => {
            return request(url, null, 'get', isBlob, background);
        };

        post = (url, data, background) => {
            const isAdmin = usr && (usr.Admin || isAdminScope(usr.Scope));
            if (isAdmin) {
                return request(url, data, 'post', background);
            }

            throw new Error("You do not have sufficient privileges to perform machine configuration");
        };

        enroll = (_ess) => {
            post('/enrollment/enroll/' + _ess).then((response) => {
                waitingSubject.next(false);
                return response;
            }).catch(err => {
                waitingSubject.next(false);
                parseError(err);
                return null;
            });
        };

        enable = (_ess) => {
            post('/enrollment/enable/' + _ess).then((response) => {
                waitingSubject.next(false);
                return response;
            }).catch(err => {
                waitingSubject.next(false);
                parseError(err);
                return null;
            });
        };

        unenroll = (_ess) => {
            post('/enrollment/unenroll/' + _ess).then((response) => {
                waitingSubject.next(false);
                return response;
            }).catch(err => {
                waitingSubject.next(false);
                parseError(err);
                return null;
            });
        };

        ignoreEss = (_ess) => {
            return post('/enrollment/ignore/' + _ess);
        };

        setModalState = (type, _arg) => {
            if(type === ModalType.REPORT && _arg !== '') {
                modalSubj.next({ show: true, host: _arg, title: 'Download Host Report', modalType: type });
            } else if(type === ModalType.PRECALL && _arg > 0) {
                modalSubj.next({show: true, host: _arg, title: 'Egm Recall Preview', modalType: type});
            } else {
                modalSubj.next({show: false, host: '', title: 'Download Host Report', modalType: ModalType.REPORT});
            }
        }

        // reporting
        const downloadReport = (url, name) => {
            waitingSubject.next(true);
            let config = {
                responseType: 'blob',
                headers: {'Authorization': usr.Token}
            };

            return api.get(url, config, {}).then(res => {
                FileDownload(res.data, name);
                waitingSubject.next(false);
            }).catch((error) => {
                if (error && error.response) {
                    if (error.response.status === 401 || error.response.status === 403) {
                        if (usr.Token) signoff();
                    }
                }
                parseError(error, 1500);
                throw (error);
            });
        };

        sessionReport = (host) => {
            return downloadReport('/reports/sessions?host=' + host, 'session.csv');
        };

        enrollmentReport = () => {
            return downloadReport('/reports/enrollment', 'enrollment.csv');
        };

        deckReport = (host) => {
            return downloadReport('/reports/deck?host=' + host, 'deck.csv');
        };

        auditReport = () => {
            return downloadReport('/reports/audits', 'audits.csv');
        }

        getWagers = () => {
            let config = {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            };
            return api.get('/wagers', config, {}).then((response) => {
                wagerSubject.next(response.data);
            }).catch(err => {
                parseError(err, 1500);
            });
        }

        getUsers = () => {
            let config = {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            };
            return api.get('/users', config, {}).then((response) => {
                userSubject.next(response.data);
            }).catch(err => {
                parseError(err, 1500);
            });
        }

        modifyUser = (user) => {
            let scopeVal = user.scope;
            if (typeof scopeVal === 'string') {
                scopeVal = UserScope[scopeVal] ?? parseInt(scopeVal, 10);
            }
            let wagerID = user.wager_id;
            if (typeof wagerID === 'string') {
                const trimmed = wagerID.trim();
                wagerID = trimmed === '' ? null : parseInt(trimmed, 10);
            }
            if (!Number.isInteger(wagerID)) {
                wagerID = null;
            }
            const payload = { ...user, scope: scopeVal, wager_id: wagerID };

            return api.post('/user/modify', payload, {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            }).then(() => {
                const nextUsers = (users || []).map((u) => (u && u.id === payload.id ? payload : u));
                users = nextUsers;
                userSubject.next(nextUsers);
                if (usr && payload.id === usr.EmployeeID) {
                    const nextScope = Number.isInteger(payload.scope) ? payload.scope : usr.Scope;
                    const nextAdmin = isAdminScope(nextScope);
                    usr = {...usr, Scope: nextScope, Admin: nextAdmin, WagerID: payload.wager_id ?? payload.wagerId ?? payload.WagerID ?? null};
                    persistAuth(usr);
                    loginSubject.next({success: true, admin: nextAdmin, scope: nextScope});
                }
            }).catch(err => {
                parseError(err, 1500);
                throw err;
            });
        }

        deleteUser = (userId) => {
            return api.post('/user/delete', { id: userId }, {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            }).then(() => {
                const nextUsers = (users || []).filter(u => u.id !== userId);
                users = nextUsers;
                userSubject.next(nextUsers);
            }).catch(err => {
                parseError(err, 1500);
                throw err;
            });
        }

        getConfig = () => {
            return api.get('/config', {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            }).then((response) => {
                return response.data;
            }).catch((error) => {
                if (error && error.response && error.response.status === 401) {
                    if (usr && usr.Token) signoff();
                }
                throw error;
            });
        };

        updateConfig = (payload) => {
            return api.post('/config', payload, {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            }).then((response) => {
                context = response.data;
                contextSubject.next(response.data);
                return response.data;
            }).catch((error) => {
                if (error && error.response && error.response.status === 401) {
                    if (usr && usr.Token) signoff();
                }
                throw error;
            });
        };

        getStagedConfig = () => {
            return api.get('/config/staged', {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            }).then((response) => {
                return response.data;
            }).catch((error) => {
                if (error && error.response && error.response.status === 404) {
                    return null;
                }
                if (error && error.response && error.response.status === 401) {
                    if (usr && usr.Token) signoff();
                }
                throw error;
            });
        };

        upsertStagedConfig = (payload) => {
            return api.post('/config/staged', payload, {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            }).then((response) => {
                return response.data;
            }).catch((error) => {
                if (error && error.response && error.response.status === 401) {
                    if (usr && usr.Token) signoff();
                }
                throw error;
            });
        };

        deleteStagedConfig = () => {
            return api.delete('/config/staged', {
                responseType: 'json',
                headers: {'Authorization': usr.Token}
            }).then((response) => {
                return response.data;
            }).catch((error) => {
                if (error && error.response && error.response.status === 401) {
                    if (usr && usr.Token) signoff();
                }
                throw error;
            });
        };
    };

    const activateWs = () => {
        if (!ensureAuthIsCurrent()) {
            return;
        }
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
            return;
        }
        clearWsReconnectTimer();
        startWsHealthMonitor();
        const prefix = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
        const socket = new WebSocket(prefix + window.location.host + '/ws/games?token=' + encodeURIComponent(usr.Token));
        ws = socket;
        socket.onopen = function () {
            if (ws !== socket) {
                return;
            }
            wsReconnectAttempt = 0;
            wsReconnectNoticeShown = false;
            wsLastMessageAt = Date.now();
        };
        socket.onmessage = function (event) {
            if (ws !== socket || !event.data) {
                return;
            }
            wsLastMessageAt = Date.now();
            let dat = JSON.parse(event.data);
            publishPlayersMessage(dat);
        };
        socket.onclose = function (evt) {
            if (ws === socket) {
                ws = null;
            }
            console.log("Websocket closed: " + JSON.stringify(evt, null, 2));
            if (!wsShouldReconnect) {
                return;
            }
            scheduleWsReconnect(`close:${evt.code || 'unknown'}`);
        };
        socket.onerror = function (evt) {
            console.log("Websocket error: " + JSON.stringify(evt, null, 2));
        };
    };

    // Initialization configSubject to publish the app config from server
    const initContext = () => {
        contextSubject = new BehaviorSubject({ Version: "unknown" });
        contextSubjectSubscribe = (subscriber) => {
            return contextSubject.subscribe(subscriber);
        };

        return api.get('/context').then((response) => {
            contextSubject.next(response.data);
            context = response.data;
        }).catch(err => {
            parseError(err, 1500);
            setTimeout(initContext, 2000);
        });
    };

    // Initialization code to startup server interface. This should be called at most once
    // which we ensure by returning a function 
    const init = () => {
        api = axios.create({
            baseURL: `${window.location.origin}/api`,
            timeout: 30000,
        });

        // Pinwheel handling
        waitingSubject = new BehaviorSubject(false);
        waitingSubjectSubscribe = (subscriber) => {
            return waitingSubject.subscribe(subscriber);
        };
        waitingSubject.subscribe((_isWaiting) => {
            isWaiting = _isWaiting;
        });

        // Precall handling
        precallSubj = new BehaviorSubject('');
        precallSubjSubscrbr = (subscriber) => {
            return precallSubj.subscribe(subscriber);
        }

        // Games handling
        egmSubject = new BehaviorSubject([]);
        egmSubjectSubscribe = (subscriber) => {
            return egmSubject.subscribe(subscriber);
        };
        publishPlayersMessage = (message) => {
            const result = applyPlayersMessage(egmSubject.getValue(), message);
            if (!result.changed) {
                return result;
            }
            egmSubject.next(result.players);
            publishRecallForESS(ess, result.players);
            return result;
        };

        // Wager handling
        wagerSubject = new BehaviorSubject([])
        wagerSubjectSubscribe = (subscriber) => {
            return wagerSubject.subscribe(subscriber);
        }

        // User handling
        userSubject = new BehaviorSubject([])
        userSubjectSubscribe = (subscriber) => {
            return userSubject.subscribe(subscriber);
        }
        userSubject.subscribe((v) => {
            users = v || [];
        });

        // Recall handling
        recallSubject = new BehaviorSubject({});
        recallSubjectSubscribe = (subscriber) => {
            return recallSubject.subscribe(subscriber);
        };
        recallSubject.subscribe((_recall) => {
            recall = _recall;
        });
        publishRecallForESS = (_ess, players) => {
            if (!_ess) {
                recallSubject.next({});
                return;
            }
            const nextRecall = (players || []).find((player) => player && player.ESS === _ess);
            recallSubject.next(nextRecall || {});
        };
        setRecall = (anum) => {
            if (anum > 0) {
                get('/peek/' + anum, false, true).then((res) => {
                    if (recall !== res.data) {
                        recallSubject.next({ GameRecallJson: res.data });
                        precallSubj.next(true);
                    }
                }).catch(err => {
                    parseError(err, 1500);
                });
            } else {
                recallSubject.next({});
                precallSubj.next(false);
            }
        }

        // ess handling
        essSubject = new BehaviorSubject('');
        essSubjectSubscribe = (subscriber) => {
            return essSubject.subscribe(subscriber);
        };
        essSubject.subscribe((_ess) => {
            ess = _ess;
        });
        setESS = (_ess) => {
            if (_ess !== ess) {
                essSubject.next(_ess);
                publishRecallForESS(_ess, egmSubject.getValue());
            }
        }

        // Report Modal handling
        modalSubj = new BehaviorSubject('');
        modalSubjSubscrbr = (subscriber) => {
            return modalSubj.subscribe(subscriber);
        };

        initContext();
        initlogin(); // DO this last so all subjects are defined
    };

    const notify = (typ, title, body, duration) => {
        try {
            title = title || "Unknown";
            body = body || "";
            duration = duration || 1500;
            NotificationManager[typ](body, title, duration);
        } catch (error) {
            console.log(error.data);
        }
    };

    const info = (title, body, duration) => {
        notify('info', title, body, duration);
    };
    const success = (title, body, duration) => {
        notify('success', title, body, duration);
    };
    const warning = (title, body, duration) => {
        notify('warning', title, body, duration);
    };
    const error = (title, body, duration) => {
        notify('error', title, body, duration);
    };

    const show = (err, nf, duration) => {
        let title = "Unknown Error";
        let body = "";

        try {
            if (err) {
                if (err.response && err.response.statusText) {
                    title = err.response.statusText;
                } else {
                    if (err.message) title = err.message;
                }
                if (err.response && err.response.data) body = err.response.data;
            }

            nf(title, body, duration || 1500);
        } catch (error) {
            try {
                nf(title, err.message, duration || 1500);
            } catch(error2) {
                console.log(err);
            }
        }
    };

    const parseError = (err, duration) => {
        show(err, error, duration);
    };

    const parseWarning = (err, duration) => {
        show(err, warning, duration);
    };

    const initRetStruct = () => {
        retStruct = {
            SubscribeContext: contextSubjectSubscribe,
            SubscribeLogin: loginSubjectSubscribe,
            SubscribeWaiting: waitingSubjectSubscribe,
            SubscribePrecall: precallSubjSubscrbr,
            SubscribeESS: essSubjectSubscribe,
            SubscribeEgms: egmSubjectSubscribe,
            SubscribeRecall: recallSubjectSubscribe,
            SubscribeModal: modalSubjSubscrbr,
            SubscribeWagers: wagerSubjectSubscribe,
            SubscribeUsers: userSubjectSubscribe,
            ModalType: ModalType,
            SetModalState: setModalState,
            Login: login,
            Register: usermod,
            Get: get,
            Post: post,
            SetESS: setESS,
            SetRecall: setRecall,
            NavRules: navRules,
            Enroll: enroll,
            Enable: enable,
            UnEnroll: unenroll,
            Ignore: ignoreEss,
            GetWagers: getWagers,
            GetUsers: getUsers,
            ModifyUser: modifyUser,
            DeleteUser: deleteUser,
            GetConfig: getConfig,
            UpdateConfig: updateConfig,
            GetStagedConfig: getStagedConfig,
            UpsertStagedConfig: upsertStagedConfig,
            DeleteStagedConfig: deleteStagedConfig,
            ParseError: parseError,
            ParseWarning: parseWarning,
            Info: info,
            Success: success,
            Warning: warning,
            Error: error,
            SignOff: signoff,
            SessionReport: sessionReport,
            EnrollmentReport: enrollmentReport,
            DeckReport: deckReport,
            AuditReport: auditReport,
        }
    };

    return function () {
        if (!once) {
            once = true;
            isWaiting = false;
            init();
            initRetStruct();
        }
        return retStruct;
    };
})();

export const ServerIntf = serverIntf();
